import React from 'react';
import { Loader2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';

// Aperçu du simulateur de devis, tel qu'il apparaîtra sur le site de l'établissement.
//
// Le cadre affiche le VRAI widget public, pas une imitation — sinon l'aperçu finirait par mentir. Mais
// ce widget interroge une route publique qui ne répond que pour un simulateur déjà activé, alors qu'on
// veut justement voir son parcours PENDANT qu'on le construit.
//
// D'où le montage : cet écran-ci est authentifié, il récupère la charge utile avec son jeton, puis la
// passe à l'iframe par postMessage ; la page d'aperçu s'en sert pour nourrir le widget. Le jeton ne
// quitte jamais l'application et le widget public reste inchangé.
//
// `cle` sert à forcer un rechargement : le widget lit sa configuration une fois, au chargement, il n'a
// pas d'autre moyen de se rafraîchir. On l'incrémente à chaque changement de réglage.
export function ApercuSimulateur({
    audience = '',
    forceMode = '',
    cle = 0,
}: {
    audience?: '' | 'professionnel' | 'particulier';
    forceMode?: '' | 'liste' | 'guide';
    cle?: number;
}) {
    const ref = React.useRef<HTMLIFrameElement>(null);
    const [charge, setCharge] = React.useState<unknown>(null);
    const [pret, setPret] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    // Une tentative ratée ne doit pas condamner l'apercu jusqu'au prochain reglage : le serveur peut
    // simplement avoir ete injoignable une seconde (redemarrage, reseau). Ce compteur rejoue l'appel.
    const [essai, setEssai] = React.useState(0);

    React.useEffect(() => {
        let vivant = true;
        setCharge(null); setPret(false); setErreur('');
        moduleApi.getSimulateurApercu({ audience, force_mode: forceMode })
            .then((d: any) => { if (vivant) { if (d?.error) setErreur(d.error); else setCharge(d); } })
            .catch(() => { if (vivant) setErreur('Aperçu indisponible pour le moment.'); });
        return () => { vivant = false; };
    }, [cle, audience, forceMode, essai]);

    React.useEffect(() => {
        const surMessage = (e: MessageEvent) => {
            if (e.origin !== window.location.origin) return;
            if ((e.data as any)?.type === 'iamani-simulateur-apercu-pret') setPret(true);
        };
        window.addEventListener('message', surMessage);
        return () => window.removeEventListener('message', surMessage);
    }, []);

    // L'iframe peut être prête avant ou après la réponse du serveur : on n'envoie que quand les deux
    // sont là, et cet effet se rejoue dès que l'un des deux arrive.
    React.useEffect(() => {
        if (!pret || !charge) return;
        const fenetre = ref.current?.contentWindow;
        if (!fenetre) return;
        fenetre.postMessage({ type: 'iamani-simulateur-apercu', payload: charge }, window.location.origin);
    }, [pret, charge]);

    return (
        <div className="relative w-full h-full">
            <iframe
                key={`${cle}-${essai}`}
                ref={ref}
                title="Aperçu du simulateur"
                src="/simulateur-apercu.html"
                className="w-full h-full border-0"
            />
            {!charge && !erreur && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                    <Loader2 size={18} className="animate-spin text-content-muted" />
                </div>
            )}
            {erreur && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center bg-white/90">
                    <p className="text-xs text-content-muted">{erreur}</p>
                    <button type="button" onClick={() => setEssai((n) => n + 1)}
                        className="text-xs font-bold text-brand-600 hover:text-brand-700 underline">
                        Réessayer
                    </button>
                </div>
            )}
        </div>
    );
}
